import { HStack, Text, VStack, Image, Stack } from '@skin-seoul/design-system';
import { formatCurrency, useProductService, useRegionCurrency } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';
import StatsSectionSkeleton from './StatsSection.skeleton';
import { useProductCompareContext } from '../../context';
import arrowLowPositive from './assets/arrow-low-positive.png';
import arrowHighNegative from './assets/arrow-high-negative.png';
import arrowHighPositive from './assets/arrow-high-positive.png';
import arrowLowNegative from './assets/arrow-low-negative.png';
type StatsSectionProps = {
  orgProductId: number;
  altProductId: number;
};
const StatsSection = ({
  orgProductId,
  altProductId
}: StatsSectionProps) => {
  const {
    currency
  } = useRegionCurrency();
  const {
    useGetListRecommendations
  } = useProductService();
  const {
    selectedFilter
  } = useProductCompareContext();
  const {
    data: recommendationsData,
    isLoading
  } = useGetListRecommendations({
    productId: orgProductId,
    currency,
    filter: selectedFilter
  });
  const orgProduct = recommendationsData?.original;
  const altProduct = recommendationsData?.recommendations.find(rec => (rec.parentProductId ?? rec.productId) === altProductId);
  if (isLoading || !orgProduct || !altProduct) {
    return <StatsSectionSkeleton />;
  }
  const getActualPrice = (product: typeof orgProduct) => product.salePrice ?? product.originalPrice;
  const orgPrice = getActualPrice(orgProduct);
  const altPrice = getActualPrice(altProduct);
  const priceDifference = altPrice - orgPrice;
  const popularityDifference = altProduct.popularity - orgProduct.popularity;
  const roundedPriceDiff = Number(priceDifference.toFixed(2));
  const roundedPopularityDiff = Number(popularityDifference.toFixed(1));
  const priceSign = roundedPriceDiff >= 0 ? '+' : '';
  const popularitySign = roundedPopularityDiff >= 0 ? '+' : '-';
  const shouldShowPriceArrow = roundedPriceDiff !== 0;
  const shouldShowPopularityArrow = roundedPopularityDiff !== 0;
  const priceArrowImage = roundedPriceDiff < 0 ? arrowLowPositive : arrowHighNegative;
  const popularityArrowImage = roundedPopularityDiff > 0 ? arrowHighPositive : arrowLowNegative;
  return <HStack width="full" height="100%" gap={3} alignItems="stretch" flexDirection={{
    base: 'column',
    mobile: 'row'
  }} data-sentry-element="HStack" data-sentry-component="StatsSection" data-sentry-source-file="StatsSection.tsx">
      <VStack flex={1} direction={{
      base: 'row',
      mobile: 'column'
    }} justifyContent="space-between" border="1px solid" borderColor="primary.300" background="generic.0" borderRadius="8px" overflow="hidden" alignItems={{
      base: 'center',
      mobile: 'start'
    }} gap={{
      base: 1,
      tablet: 4
    }} data-sentry-element="VStack" data-sentry-source-file="StatsSection.tsx">
        <Stack p={{
        base: '12px 16px 0 16px',
        mobile: 3
      }} width="full" height="100%" direction={{
        base: 'row',
        mobile: 'column'
      }} alignItems="stretch" justifyContent="space-between" data-sentry-element="Stack" data-sentry-source-file="StatsSection.tsx">
          <Text textStyle="paragraph.small" fontWeight="regular" color="neutral.600" data-sentry-element="Text" data-sentry-source-file="StatsSection.tsx">
            Price
          </Text>
          <Condition expression={roundedPriceDiff === 0} then={<VStack alignItems="start" gap={1}>
                <Text textStyle="heading.h4" fontWeight="bold" color="primary.400" as="h4">
                  Same
                </Text>
                <Text textStyle="paragraph.small" display={{
            base: 'none',
            mobile: 'block'
          }} fontWeight="regular" color="neutral.600">
                  No difference
                </Text>
              </VStack>} else={<VStack alignItems="start" gap={1}>
                <HStack gap={2} alignItems="center">
                  <Text textStyle={{
              base: 'heading.h5',
              mobile: 'heading.h4'
            }} fontWeight="bold" color="primary.400" as="h4">
                    {priceSign}
                    {formatCurrency(currency, Math.abs(roundedPriceDiff))}
                  </Text>
                  <Condition expression={shouldShowPriceArrow} then={<Image src={priceArrowImage.src} alt="Price trend" width={{
              base: '28px',
              tablet: '36px'
            }} height={{
              base: '28px',
              tablet: '36px'
            }} />} />
                </HStack>
                <Text textStyle="paragraph.small" display={{
            base: 'none',
            mobile: 'block'
          }} fontWeight="regular" color="neutral.600">
                  {roundedPriceDiff > 0 ? 'Premium-priced' : 'Smartly priced'}
                </Text>
              </VStack>} data-sentry-element="Condition" data-sentry-source-file="StatsSection.tsx" />
        </Stack>
        <HStack display={{
        base: 'flex',
        mobile: 'none'
      }} background="neutral.50" paddingX={4} paddingY={0.5} gap={2} width="full" alignItems="center" justifyContent="center" data-sentry-element="HStack" data-sentry-source-file="StatsSection.tsx">
          <Text textStyle="paragraph.xsmall" fontWeight="semibold" color="neutral.400" data-sentry-element="Text" data-sentry-source-file="StatsSection.tsx">
            {formatCurrency(currency, orgPrice)}
          </Text>
          <Text textStyle="paragraph.xsmall" fontWeight="semibold" color="neutral.800" data-sentry-element="Text" data-sentry-source-file="StatsSection.tsx">
            vs
          </Text>
          <Text textStyle="paragraph.xsmall" fontWeight="semibold" color="primary.400" data-sentry-element="Text" data-sentry-source-file="StatsSection.tsx">
            {formatCurrency(currency, altPrice)}
          </Text>
        </HStack>
      </VStack>

      <Stack flex={1} direction={{
      base: 'row',
      mobile: 'column'
    }} justifyContent="space-between" border="1px solid" borderColor="primary.300" background="generic.0" borderRadius="8px" padding={3} alignItems={{
      base: 'center',
      mobile: 'start'
    }} gap={{
      base: 1,
      tablet: 4
    }} data-sentry-element="Stack" data-sentry-source-file="StatsSection.tsx">
        <Text textStyle="paragraph.small" fontWeight="regular" color="neutral.600" data-sentry-element="Text" data-sentry-source-file="StatsSection.tsx">
          Popularity
        </Text>
        <Condition expression={roundedPopularityDiff === 0} then={<VStack alignItems="start" gap={1}>
              <Text textStyle="heading.h4" fontWeight="bold" color="primary.400" as="h4">
                Same
              </Text>
              <Text textStyle="paragraph.small" display={{
          base: 'none',
          mobile: 'block'
        }} fontWeight="regular" color="neutral.600">
                No difference
              </Text>
            </VStack>} else={<VStack alignItems="start" gap={1}>
              <HStack gap={2} alignItems="center">
                <Text textStyle={{
            base: 'heading.h5',
            mobile: 'heading.h4'
          }} fontWeight="bold" color="primary.400" as="h4">
                  {popularitySign}
                  {Math.abs(roundedPopularityDiff).toFixed(1)}%
                </Text>
                <Condition expression={shouldShowPopularityArrow} then={<Image src={popularityArrowImage.src} alt="Popularity trend" width={{
            base: '28px',
            tablet: '36px'
          }} height={{
            base: '28px',
            tablet: '36px'
          }} />} />
              </HStack>
              <Text textStyle="paragraph.small" display={{
          base: 'none',
          mobile: 'block'
        }} fontWeight="regular" color="neutral.600">
                {roundedPopularityDiff > 0 ? 'Widely favored' : 'Subtly recognized'}
              </Text>
            </VStack>} data-sentry-element="Condition" data-sentry-source-file="StatsSection.tsx" />
      </Stack>
    </HStack>;
};
StatsSection.Skeleton = StatsSectionSkeleton;
export default StatsSection;